import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, ArrowBack, Delete, Save} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {buildAttachmentRef} from 'Hooks/useAttachmentTokens';
import {
    EUserGroup,
    ExampleSnippetDto,
    GetFileTypeData,
    GetProductExampleData,
    ProductExampleAttachmentDto,
    ProductExampleContentType,
    ProductExampleStatus,
    RelatedExampleLinkDto,
    SnippetFormat,
    UpdateProductExampleModel,
} from 'apiClient/Api';
import {MarkdownEditor} from 'Components/ProductExamples/MarkdownEditor';
import {AttachmentManager} from 'Components/ProductExamples/AttachmentManager';

interface SnippetDraft {
    localKey: string;
    serverId: string | null | undefined;
    title: string;
    description: string;
    format: SnippetFormat;
    content: string;
}

interface FormState {
    fileTypeId: string;
    title: string;
    description: string;
    contentType: ProductExampleContentType;
    content: string;
    features: string[];
    targetVersion: string;
    status: ProductExampleStatus;
    notes: string;
    snippets: SnippetDraft[];
    relatedExampleIds: string[];
}

const emptyForm: FormState = {
    fileTypeId: '',
    title: '',
    description: '',
    contentType: ProductExampleContentType.Configuration,
    content: '',
    features: [],
    targetVersion: '',
    status: ProductExampleStatus.Draft,
    notes: '',
    snippets: [],
    relatedExampleIds: [],
};

const newLocalKey = (() => {
    let counter = 0;
    return () => `local-${++counter}`;
})();

const toForm = (data: GetProductExampleData): FormState => ({
    fileTypeId: data.fileTypeId ?? '',
    title: data.title ?? '',
    description: data.description ?? '',
    contentType: data.contentType ?? ProductExampleContentType.Configuration,
    content: data.content ?? '',
    features: data.features ?? [],
    targetVersion: data.targetVersion ?? '',
    status: data.status ?? ProductExampleStatus.Draft,
    notes: data.notes ?? '',
    snippets: (data.snippets ?? []).map(s => ({
        localKey: s.id ?? newLocalKey(),
        serverId: s.id ?? null,
        title: s.title,
        description: s.description,
        format: s.format ?? SnippetFormat.Xml,
        content: s.content,
    })),
    relatedExampleIds: (data.relatedExamples ?? []).map(r => r.id!).filter(Boolean),
});

const toApiSnippets = (snippets: SnippetDraft[]): ExampleSnippetDto[] =>
    snippets.map(s => ({
        id: s.serverId ?? null,
        title: s.title,
        description: s.description,
        format: s.format,
        content: s.content,
    }));

/** Maps the snippet format enum to the Monaco language id. */
const monacoLanguage = (format: SnippetFormat): string => {
    switch (format) {
        case SnippetFormat.Json: return 'json';
        case SnippetFormat.Yaml: return 'yaml';
        case SnippetFormat.Ini: return 'ini';
        case SnippetFormat.PlainText: return 'plaintext';
        case SnippetFormat.Xml:
        default: return 'xml';
    }
};

const customerImpliesCustomerAttachments = (status: ProductExampleStatus): EUserGroup =>
    status === ProductExampleStatus.Customer ? EUserGroup.CUSTOMER : EUserGroup.OFFICE;

/**
 * Editor for a product example. Creation uses the draft flow: the list page
 * posts /ProductExample/draft with a FileType and navigates the user here
 * with the returned id. From that point on, saves are PUTs against the same
 * id — so attachments can be uploaded immediately.
 */
const ProductExampleEdit: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('productExamples.edit.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {id} = useParams<{id: string}>();

    const [form, setForm] = useState<FormState>(emptyForm);
    const [attachments, setAttachments] = useState<ProductExampleAttachmentDto[]>([]);
    const [related, setRelated] = useState<RelatedExampleLinkDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [products, setProducts] = useState<GetFileTypeData[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [relatedOptions, setRelatedOptions] = useState<RelatedExampleLinkDto[]>([]);
    const [mainTab, setMainTab] = useState<'content' | 'snippets' | 'attachments' | 'related'>('content');

    const dirtyRef = useRef(false);
    const markDirty = () => { dirtyRef.current = true; };

    const loadProducts = useCallback(async () => {
        setProductsLoading(true);
        try {
            const response = await context.api.fileType.fileTypeGetFileTypes();
            setProducts(response.data ?? []);
        } catch {
            // picker stays empty; surface only via product field validation
        } finally {
            setProductsLoading(false);
        }
    }, [context.api.fileType]);

    const loadRelatedOptions = useCallback(async (fileTypeId: string | undefined) => {
        try {
            const response = await context.api.productExample.productExampleList({
                status: ProductExampleStatus.Approved,
                fileTypeId: fileTypeId || undefined,
            });
            const mapped: RelatedExampleLinkDto[] = (response.data ?? [])
                .filter(item => item.id && item.id !== id)
                .map(item => ({
                    id: item.id,
                    title: item.title,
                    fileTypeName: item.fileTypeName,
                    status: item.status,
                    contentType: item.contentType,
                }));
            setRelatedOptions(mapped);
        } catch {
            setRelatedOptions([]);
        }
    }, [context.api.productExample, id]);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        try {
            const response = await context.api.productExample.productExampleGet(id);
            setForm(toForm(response.data));
            setAttachments(response.data.attachments ?? []);
            setRelated(response.data.relatedExamples ?? []);
            dirtyRef.current = false;
        } catch {
            setLoadError(t('productExamples.edit.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.productExample, id, t]);

    useEffect(() => { loadProducts(); }, [loadProducts]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadRelatedOptions(form.fileTypeId); }, [form.fileTypeId, loadRelatedOptions]);

    const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        markDirty();
        setForm(prev => ({...prev, [key]: value}));
    };

    const updateSnippet = (localKey: string, patch: Partial<SnippetDraft>) => {
        markDirty();
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.map(s => s.localKey === localKey ? {...s, ...patch} : s),
        }));
    };

    const addSnippet = () => {
        markDirty();
        setForm(prev => ({
            ...prev,
            snippets: [
                ...prev.snippets,
                {
                    localKey: newLocalKey(),
                    serverId: null,
                    title: '',
                    description: '',
                    format: SnippetFormat.Xml,
                    content: '',
                },
            ],
        }));
    };

    const removeSnippet = (localKey: string) => {
        markDirty();
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.filter(s => s.localKey !== localKey),
        }));
    };

    const validate = (): string | null => {
        if (!form.fileTypeId) return t('productExamples.edit.validation.productRequired');
        if (!form.title.trim()) return t('productExamples.edit.validation.titleRequired');
        if (!form.description.trim()) return t('productExamples.edit.validation.descriptionRequired');
        for (const s of form.snippets) {
            if (!s.title.trim()) return t('productExamples.edit.validation.snippetTitleRequired');
            if (!s.description.trim()) return t('productExamples.edit.validation.snippetDescriptionRequired');
            if (!s.content.trim()) return t('productExamples.edit.validation.snippetContentRequired');
        }
        return null;
    };

    const save = async () => {
        if (!id) return;
        const err = validate();
        if (err) {
            setValidationError(err);
            return;
        }
        setValidationError(null);
        setSaving(true);
        try {
            const payload: UpdateProductExampleModel = {
                fileTypeId: form.fileTypeId,
                title: form.title.trim(),
                description: form.description.trim(),
                contentType: form.contentType,
                content: form.content.trim() ? form.content : null,
                features: form.features,
                targetVersion: form.targetVersion.trim() || null,
                status: form.status,
                notes: form.notes.trim() || null,
                snippets: toApiSnippets(form.snippets),
                relatedExampleIds: form.relatedExampleIds,
            };
            const response = await context.api.productExample.productExampleUpdate(id, payload);
            enqueueSnackbar(t('productExamples.edit.saved'), {variant: 'success'});
            setForm(toForm(response.data));
            setAttachments(response.data.attachments ?? []);
            setRelated(response.data.relatedExamples ?? []);
            dirtyRef.current = false;
        } catch (e: any) {
            const msg = e?.response?.data?.detail ?? e?.message ?? t('productExamples.edit.saveError');
            enqueueSnackbar(msg, {variant: 'error'});
        } finally {
            setSaving(false);
        }
    };

    const discardDraft = async () => {
        if (!id) return;
        if (!window.confirm(t('productExamples.edit.discardConfirm'))) return;
        try {
            await context.api.productExample.productExampleDelete(id);
            dirtyRef.current = false;
            enqueueSnackbar(t('productExamples.edit.discarded'), {variant: 'info'});
            navigate('/admin/product-examples');
        } catch {
            enqueueSnackbar(t('productExamples.edit.discardError'), {variant: 'error'});
        }
    };

    const selectedProduct = products.find(p => p.id === form.fileTypeId) ?? null;

    const contentTypeOptions = useMemo(() => [
        { value: ProductExampleContentType.Configuration, label: t('productExamples.contentType.configuration') },
        { value: ProductExampleContentType.Article, label: t('productExamples.contentType.article') },
        { value: ProductExampleContentType.HowTo, label: t('productExamples.contentType.howTo') },
        { value: ProductExampleContentType.Troubleshooting, label: t('productExamples.contentType.troubleshooting') },
    ], [t]);

    const handleAttachmentsChange = (next: ProductExampleAttachmentDto[]) => {
        setAttachments(next);
        markDirty();
    };

    const handleAttachmentReplaced = (oldId: string, next: ProductExampleAttachmentDto) => {
        if (!next.id) return;
        const oldRef = buildAttachmentRef(oldId);
        const newRef = buildAttachmentRef(next.id);
        if (form.content.includes(oldRef)) {
            markDirty();
            setForm(prev => ({ ...prev, content: prev.content.split(oldRef).join(newRef) }));
        }
    };

    const insertAttachmentRef = (att: ProductExampleAttachmentDto) => {
        if (!att.id) return;
        const snippet = att.contentType?.startsWith('image/')
            ? `\n\n![${att.fileName ?? ''}](${buildAttachmentRef(att.id)})\n\n`
            : `[${att.fileName ?? ''}](${buildAttachmentRef(att.id)})`;
        markDirty();
        setForm(prev => ({ ...prev, content: prev.content + snippet }));
        setMainTab('content');
    };

    const uploadFromEditor = useCallback(async (file: File): Promise<ProductExampleAttachmentDto | null> => {
        if (!id) return null;
        try {
            const fallbackGroup = customerImpliesCustomerAttachments(form.status);
            const response = await context.api.productExample.productExampleUploadAttachment(id, {
                File: file,
                RequiredUserGroup: EUserGroup[fallbackGroup],
            });
            const att = response.data;
            if (att) {
                setAttachments(prev => [...prev, att]);
                markDirty();
            }
            return att ?? null;
        } catch (err: any) {
            enqueueSnackbar(err?.response?.data?.detail ?? t('productExamples.attachments.uploadError', { name: file.name }), { variant: 'error' });
            return null;
        }
    }, [context.api.productExample, enqueueSnackbar, form.status, id, t]);

    const selectedRelated: RelatedExampleLinkDto[] = useMemo(() => {
        const fromLoaded = new Map(related.map(r => [r.id!, r]));
        return form.relatedExampleIds
            .map(rid => fromLoaded.get(rid) ?? relatedOptions.find(o => o.id === rid))
            .filter((r): r is RelatedExampleLinkDto => !!r);
    }, [form.relatedExampleIds, related, relatedOptions]);

    if (!id) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">{t('productExamples.edit.noDraft')}</Alert>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/admin/product-examples')}>
                    {t('productExamples.edit.back')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{width: '100%', minWidth: 0, p: 2}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <Tooltip title={t('productExamples.edit.back')}>
                        <IconButton onClick={() => navigate(`/admin/product-examples/${id}`)}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" component="h1">{t('productExamples.edit.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('productExamples.edit.subtitle')}</Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                    {form.status === ProductExampleStatus.Draft && (
                        <Button color="error" variant="text" onClick={discardDraft}>
                            {t('productExamples.edit.discard')}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16}/> : <Save/>}
                        disabled={saving || loading}
                        onClick={save}
                    >
                        {t('productExamples.edit.save')}
                    </Button>
                </Stack>
            </Stack>

            {loadError && <Alert severity="error" sx={{mb: 2}}>{loadError}</Alert>}
            {validationError && <Alert severity="warning" sx={{mb: 2}}>{validationError}</Alert>}

            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Stack spacing={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="h6">{t('productExamples.edit.metadata')}</Typography>

                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    <Autocomplete<GetFileTypeData>
                                        sx={{flex: 2}}
                                        options={products}
                                        loading={productsLoading}
                                        value={selectedProduct}
                                        onChange={(_, value) => updateField('fileTypeId', value?.id ?? '')}
                                        getOptionLabel={(option) => option.name ?? ''}
                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('productExamples.fields.product')}
                                                required
                                            />
                                        )}
                                    />
                                    <FormControl sx={{flex: 1, minWidth: 180}}>
                                        <InputLabel id="product-example-content-type">{t('productExamples.fields.contentType')}</InputLabel>
                                        <Select
                                            labelId="product-example-content-type"
                                            label={t('productExamples.fields.contentType')}
                                            value={form.contentType}
                                            onChange={(e) => updateField('contentType', e.target.value as ProductExampleContentType)}
                                        >
                                            {contentTypeOptions.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{flex: 1, minWidth: 160}}>
                                        <InputLabel id="product-example-status">{t('productExamples.fields.status')}</InputLabel>
                                        <Select
                                            labelId="product-example-status"
                                            label={t('productExamples.fields.status')}
                                            value={form.status}
                                            onChange={(e) => updateField('status', e.target.value as ProductExampleStatus)}
                                        >
                                            <MenuItem value={ProductExampleStatus.Draft}>{t('productExamples.status.draft')}</MenuItem>
                                            <MenuItem value={ProductExampleStatus.Approved}>{t('productExamples.status.approved')}</MenuItem>
                                            <MenuItem value={ProductExampleStatus.Customer}>{t('productExamples.status.customer')}</MenuItem>
                                            <MenuItem value={ProductExampleStatus.Deprecated}>{t('productExamples.status.deprecated')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <TextField
                                    label={t('productExamples.fields.title')}
                                    value={form.title}
                                    onChange={(e) => updateField('title', e.target.value)}
                                    required
                                    fullWidth
                                    slotProps={{htmlInput: {maxLength: 200}}}
                                />
                                <TextField
                                    label={t('productExamples.fields.description')}
                                    value={form.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    required
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('productExamples.fields.descriptionHelper')}
                                />
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[] as string[]}
                                        value={form.features}
                                        onChange={(_, value) => updateField('features', value as string[])}
                                        sx={{flex: 2}}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('productExamples.fields.features')}
                                                placeholder={t('productExamples.fields.featuresPlaceholder')}
                                                helperText={t('productExamples.fields.featuresHelper')}
                                            />
                                        )}
                                    />
                                    <TextField
                                        label={t('productExamples.fields.targetVersion')}
                                        value={form.targetVersion}
                                        onChange={(e) => updateField('targetVersion', e.target.value)}
                                        sx={{flex: 1}}
                                        slotProps={{htmlInput: {maxLength: 50}}}
                                    />
                                </Stack>
                                <TextField
                                    label={t('productExamples.fields.notes')}
                                    value={form.notes}
                                    onChange={(e) => updateField('notes', e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('productExamples.fields.notesHelper')}
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Paper variant="outlined">
                        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Tab value="content" label={t('productExamples.edit.tabContent')}/>
                            <Tab value="snippets" label={`${t('productExamples.edit.tabSnippets')} (${form.snippets.length})`}/>
                            <Tab value="attachments" label={`${t('productExamples.edit.tabAttachments')} (${attachments.length})`}/>
                            <Tab value="related" label={`${t('productExamples.edit.tabRelated')} (${form.relatedExampleIds.length})`}/>
                        </Tabs>

                        <Box sx={{ p: 2 }}>
                            {mainTab === 'content' && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('productExamples.edit.contentHelper')}
                                    </Typography>
                                    <MarkdownEditor
                                        value={form.content}
                                        onChange={(v) => updateField('content', v)}
                                        exampleId={id}
                                        attachments={attachments}
                                        onUpload={uploadFromEditor}
                                        label={t('productExamples.fields.content')}
                                    />
                                </Box>
                            )}

                            {mainTab === 'snippets' && (
                                <Box>
                                    <Stack direction="row" spacing={2} sx={{alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('productExamples.edit.snippetsHelper')}
                                        </Typography>
                                        <Button startIcon={<Add/>} onClick={addSnippet}>
                                            {t('productExamples.edit.addSnippet')}
                                        </Button>
                                    </Stack>

                                    {form.snippets.length === 0 && (
                                        <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
                                            <Typography color="text.secondary" sx={{mb: 2}}>
                                                {t('productExamples.edit.noSnippets')}
                                            </Typography>
                                            <Button variant="outlined" startIcon={<Add/>} onClick={addSnippet}>
                                                {t('productExamples.edit.addFirstSnippet')}
                                            </Button>
                                        </Paper>
                                    )}

                                    <Stack spacing={2}>
                                        {form.snippets.map((snippet, index) => (
                                            <Card key={snippet.localKey} variant="outlined">
                                                <CardContent>
                                                    <Stack spacing={2}>
                                                        <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                                            <Typography variant="subtitle1" sx={{fontWeight: 600, flex: 1}}>
                                                                {t('productExamples.edit.snippetNumber', {index: index + 1})}
                                                            </Typography>
                                                            {snippet.serverId && (
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`ID: ${snippet.serverId.slice(0, 8)}…`}
                                                                    sx={{fontFamily: 'monospace'}}
                                                                />
                                                            )}
                                                            <Tooltip title={t('productExamples.edit.removeSnippet')}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => removeSnippet(snippet.localKey)}
                                                                >
                                                                    <Delete fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>

                                                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                                            <TextField
                                                                label={t('productExamples.fields.snippetTitle')}
                                                                value={snippet.title}
                                                                onChange={(e) => updateSnippet(snippet.localKey, {title: e.target.value})}
                                                                required
                                                                size="small"
                                                                sx={{flex: 2}}
                                                                slotProps={{htmlInput: {maxLength: 200}}}
                                                            />
                                                            <FormControl size="small" sx={{minWidth: 140}}>
                                                                <InputLabel id={`format-${snippet.localKey}`}>
                                                                    {t('productExamples.fields.snippetFormat')}
                                                                </InputLabel>
                                                                <Select
                                                                    labelId={`format-${snippet.localKey}`}
                                                                    label={t('productExamples.fields.snippetFormat')}
                                                                    value={snippet.format}
                                                                    onChange={(e) => updateSnippet(snippet.localKey, {format: e.target.value as SnippetFormat})}
                                                                >
                                                                    <MenuItem value={SnippetFormat.Xml}>XML</MenuItem>
                                                                    <MenuItem value={SnippetFormat.Json}>JSON</MenuItem>
                                                                    <MenuItem value={SnippetFormat.Yaml}>YAML</MenuItem>
                                                                    <MenuItem value={SnippetFormat.Ini}>INI</MenuItem>
                                                                    <MenuItem value={SnippetFormat.PlainText}>{t('productExamples.format.plainText')}</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </Stack>

                                                        <TextField
                                                            label={t('productExamples.fields.snippetDescription')}
                                                            value={snippet.description}
                                                            onChange={(e) => updateSnippet(snippet.localKey, {description: e.target.value})}
                                                            required
                                                            fullWidth
                                                            size="small"
                                                            multiline
                                                            minRows={2}
                                                            maxRows={4}
                                                        />

                                                        <Divider/>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                                                                {t('productExamples.fields.snippetContent')}
                                                            </Typography>
                                                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                                                <Editor
                                                                    height="320px"
                                                                    language={monacoLanguage(snippet.format)}
                                                                    value={snippet.content}
                                                                    onChange={(value) => updateSnippet(snippet.localKey, {content: value ?? ''})}
                                                                    options={{
                                                                        minimap: {enabled: false},
                                                                        scrollBeyondLastLine: false,
                                                                        automaticLayout: true,
                                                                        fontSize: 13,
                                                                        wordWrap: 'on',
                                                                        lineNumbers: 'on',
                                                                        folding: true,
                                                                        formatOnPaste: true,
                                                                        tabSize: 2,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {mainTab === 'attachments' && (
                                <AttachmentManager
                                    exampleId={id}
                                    attachments={attachments}
                                    onChange={handleAttachmentsChange}
                                    onAttachmentReplaced={handleAttachmentReplaced}
                                    onInsertRef={insertAttachmentRef}
                                    defaultUserGroup={customerImpliesCustomerAttachments(form.status)}
                                />
                            )}

                            {mainTab === 'related' && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('productExamples.edit.relatedHelper')}
                                    </Typography>
                                    <Autocomplete<RelatedExampleLinkDto, true>
                                        multiple
                                        options={relatedOptions}
                                        value={selectedRelated}
                                        onChange={(_, value) => updateField('relatedExampleIds', value.map(v => v.id!).filter(Boolean))}
                                        getOptionLabel={(option) => `${option.title ?? '—'} (${option.fileTypeName ?? ''})`}
                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('productExamples.fields.related')}
                                                placeholder={t('productExamples.fields.relatedPlaceholder')}
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Stack>
            )}
        </Box>
    );
};

export default ProductExampleEdit;
